/**
 * My Jobs Page - Studio's job listings (past and present)
 */
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NavBar, NavActions } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";
import { 
  ArrowLeft, Plus, Calendar, MapPin, DollarSign, Users, 
  Briefcase, Camera, Video, CheckCircle, Clock, ChevronRight, AlertTriangle,
  Archive, XCircle, UserCheck, RotateCcw
} from "lucide-react";

export default function MyJobsPage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [archivedJobs, setArchivedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    if (!user || !token) {
      navigate("/login");
      return;
    }
    
    // Only studios can access this page
    if (!user.roles?.includes("studio")) {
      navigate("/dashboard");
      return;
    }
    
    fetchJobs();
  }, [user, token]);

  async function fetchJobs() {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [mainRes, archivedRes] = await Promise.all([
        fetch(`${API}/jobs/my-jobs`, { headers }),
        fetch(`${API}/jobs/archived`, { headers }),
      ]);
      
      if (mainRes.ok) {
        const data = await mainRes.json();
        setJobs(data.jobs || []);
      }
      if (archivedRes.ok) {
        const data = await archivedRes.json();
        setArchivedJobs(data.jobs || []);
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleAction(jobId, action) {
    const actionMap = {
      "mark-hired": { url: `${API}/jobs/${jobId}/mark-hired`, method: "POST", confirm: "Mark this job as hired? (e.g., if the candidate accepted outside the platform)", success: "Job marked as hired" },
      "mark-completed": { url: `${API}/jobs/${jobId}/mark-completed`, method: "POST", confirm: "Mark this job as completed?", success: "Job marked as completed" },
      "withdraw": { url: `${API}/jobs/${jobId}/withdraw-job`, method: "POST", confirm: "Withdraw this job? It will be moved to the archive.", success: "Job withdrawn and archived" },
      "reopen": { url: `${API}/jobs/${jobId}/reopen`, method: "POST", confirm: "Reopen this job?", success: "Job reopened" },
    };
    const cfg = actionMap[action];
    if (!cfg || !window.confirm(cfg.confirm)) return;
    
    try {
      const res = await fetch(cfg.url, {
        method: cfg.method,
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: action === "mark-hired" ? JSON.stringify({}) : undefined,
      });
      if (res.ok) {
        toast.success(cfg.success);
        fetchJobs();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Action failed");
      }
    } catch {
      toast.error("Network error");
    }
  }

  // Filter jobs by status
  const openJobs = jobs.filter(j => j.status === "open");
  const filledJobs = jobs.filter(j => j.status === "filled");
  const pendingJobs = jobs.filter(j => j.status === "pending_confirmation");
  const completedJobs = jobs.filter(j => j.status === "completed");
  const expiredJobs = jobs.filter(j => j.status === "expired");

  // Sort by date (most recent first)
  const sortByDate = (jobsList) => {
    return [...jobsList].sort((a, b) => {
      const dateA = new Date(a.date || a.created_at || 0);
      const dateB = new Date(b.date || b.created_at || 0);
      return dateB - dateA;
    });
  };

  const filteredJobs = sortByDate(
    activeTab === "all" ? jobs 
    : activeTab === "open" ? openJobs 
    : activeTab === "pending" ? pendingJobs
    : activeTab === "filled" ? filledJobs 
    : activeTab === "completed" ? completedJobs
    : activeTab === "expired" ? expiredJobs
    : activeTab === "archived" ? archivedJobs
    : jobs
  );

  function getStatusBadge(status) {
    switch (status) {
      case "open":
        return <Badge className="bg-white/10 text-white border-white/20">Open</Badge>;
      case "pending_confirmation":
        return <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">Pending Hire</Badge>;
      case "filled":
        return <Badge className="bg-green-500/10 text-green-400 border-green-500/20">Filled</Badge>;
      case "completed":
        return <Badge className="bg-white/5 text-white/50 border-white/10">Completed</Badge>;
      case "expired":
        return <Badge className="bg-amber-500/10 text-amber-400/70 border-amber-500/20">Expired</Badge>;
      case "withdrawn":
        return <Badge className="bg-white/5 text-white/30 border-white/10">Withdrawn</Badge>;
      case "cancelled":
        return <Badge className="bg-red-500/10 text-red-400 border-red-500/20">Cancelled</Badge>;
      case "pending_payment":
        // Fell through to the default below, which printed the raw field —
        // studios saw a grey "pending_payment" and no hint their job was
        // invisible to everyone.
        return <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">Not published</Badge>;
      default:
        return <Badge className="bg-white/10 text-white/50">{status}</Badge>;
    }
  }

  function getJobTypeIcon(type) {
    if (type === "photo") return <Camera className="w-4 h-4" />;
    if (type === "video") return <Video className="w-4 h-4" />;
    return <Briefcase className="w-4 h-4" />;
  }

  function formatDate(dateStr) {
    if (!dateStr) return "";
    const datePart = dateStr.split("T")[0];
    const parts = datePart.split("-");
    const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  function getQuickActions(job) {
    const actions = [];
    if (job.status === "open") {
      actions.push({ label: "Mark Hired", action: "mark-hired", icon: UserCheck });
      actions.push({ label: "Withdraw", action: "withdraw", icon: Archive, variant: "destructive" });
    }
    if (job.status === "pending_confirmation") {
      actions.push({ label: "Mark Hired", action: "mark-hired", icon: UserCheck });
      actions.push({ label: "Withdraw", action: "withdraw", icon: Archive, variant: "destructive" });
    }
    if (job.status === "filled") {
      actions.push({ label: "Mark Completed", action: "mark-completed", icon: CheckCircle });
    }
    if (job.status === "expired") {
      actions.push({ label: "Reopen", action: "reopen", icon: RotateCcw });
      actions.push({ label: "Withdraw", action: "withdraw", icon: Archive, variant: "destructive" });
    }
    if (job.status === "cancelled") {
      actions.push({ label: "Reopen", action: "reopen", icon: RotateCcw });
    }
    return actions;
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <NavBar>
        <Link to="/dashboard" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">
          Dashboard
        </Link>
        <NavActions />
      </NavBar>

      <main className="flex-1 pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <Link to="/dashboard" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-4">
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm uppercase tracking-wider">Back to Dashboard</span>
              </Link>
              <h1 className="text-3xl md:text-4xl font-bold">Your Job Listings</h1>
              <p className="text-white/50 mt-2">Manage all your job postings</p>
            </div>
            <Link to="/jobs/create">
              <Button className="bg-[#3498DB] hover:bg-[#2980B9] text-white">
                <Plus className="w-4 h-4 mr-2" />
                Post New Job
              </Button>
            </Link>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-8">
            <Card className="bg-white/[0.03] border-white/10 p-4 text-center">
              <div className="text-2xl font-bold text-white">{openJobs.length}</div>
              <div className="text-white/50 text-xs">Open</div>
            </Card>
            <Card className="bg-white/[0.03] border-white/10 p-4 text-center">
              <div className="text-2xl font-bold text-amber-400">{pendingJobs.length}</div>
              <div className="text-white/50 text-xs">Pending</div>
            </Card>
            <Card className="bg-white/[0.03] border-white/10 p-4 text-center">
              <div className="text-2xl font-bold text-green-400">{filledJobs.length}</div>
              <div className="text-white/50 text-xs">Filled</div>
            </Card>
            <Card className="bg-white/[0.03] border-white/10 p-4 text-center">
              <div className="text-2xl font-bold text-white/50">{completedJobs.length}</div>
              <div className="text-white/50 text-xs">Completed</div>
            </Card>
            <Card className="bg-white/[0.03] border-white/10 p-4 text-center">
              <div className="text-2xl font-bold text-amber-400/70">{expiredJobs.length}</div>
              <div className="text-white/50 text-xs">Expired</div>
            </Card>
            <Card className="bg-white/[0.03] border-white/10 p-4 text-center">
              <div className="text-2xl font-bold text-white/30">{archivedJobs.length}</div>
              <div className="text-white/50 text-xs">Archived</div>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-white/[0.03] border border-white/10 mb-6 flex-wrap h-auto py-1">
              <TabsTrigger value="all" className="data-[state=active]:bg-white data-[state=active]:text-black">
                All ({jobs.length})
              </TabsTrigger>
              <TabsTrigger value="open" className="data-[state=active]:bg-white data-[state=active]:text-black">
                Open ({openJobs.length})
              </TabsTrigger>
              <TabsTrigger value="pending" className="data-[state=active]:bg-white data-[state=active]:text-black">
                Pending ({pendingJobs.length})
              </TabsTrigger>
              <TabsTrigger value="filled" className="data-[state=active]:bg-white data-[state=active]:text-black">
                Filled ({filledJobs.length})
              </TabsTrigger>
              <TabsTrigger value="completed" className="data-[state=active]:bg-white data-[state=active]:text-black">
                Done ({completedJobs.length})
              </TabsTrigger>
              <TabsTrigger value="expired" className="data-[state=active]:bg-white data-[state=active]:text-black">
                Expired ({expiredJobs.length})
              </TabsTrigger>
              <TabsTrigger value="archived" className="data-[state=active]:bg-white data-[state=active]:text-black">
                <Archive className="w-3.5 h-3.5 mr-1" />
                Archived ({archivedJobs.length})
              </TabsTrigger>
            </TabsList>

            {/* Job List */}
            {loading ? (
              <div className="text-center py-12 text-white/50">Loading jobs...</div>
            ) : filteredJobs.length === 0 ? (
              <Card className="bg-white/[0.03] border-white/10 p-12 text-center">
                <Briefcase className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {activeTab === "all" ? "No Jobs Yet" : activeTab === "archived" ? "No archived jobs" : `No ${activeTab} jobs`}
                </h3>
                <p className="text-white/50 mb-6">
                  {activeTab === "all" 
                    ? "Create your first job listing to start finding talented assistants."
                    : activeTab === "archived"
                    ? "Withdrawn jobs will appear here."
                    : `You don't have any ${activeTab} jobs at the moment.`}
                </p>
                {activeTab === "all" && (
                  <Link to="/jobs/create">
                    <Button className="bg-[#3498DB] hover:bg-[#2980B9] text-white">
                      <Plus className="w-4 h-4 mr-2" />
                      Post Your First Job
                    </Button>
                  </Link>
                )}
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredJobs.map((job) => {
                  const actions = getQuickActions(job);
                  return (
                    <Card key={job.job_id} className="bg-white/[0.03] border-white/10 p-6 hover:bg-white/[0.06] hover:border-white/20 transition-all" data-testid={"myjob-card-" + job.job_id}>
                      <div className="flex items-start justify-between gap-4">
                        <Link to={`/jobs/${job.job_id}`} className="flex-1 cursor-pointer">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-white/40">
                              {getJobTypeIcon(job.job_type)}
                            </span>
                            <h3 className="text-lg font-semibold">{job.title}</h3>
                            {getStatusBadge(job.status)}
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-white/50 text-sm mt-3">
                            {job.date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {formatDate(job.date)}
                              </span>
                            )}
                            {(job.location_info?.city || job.location?.city) && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {job.location_info?.city || job.location?.city}{job.location_info?.state ? ", " + job.location_info.state : ""}
                              </span>
                            )}
                            {(job.hourly_rate || job.flat_rate) && (
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-4 h-4" />
                                {job.hourly_rate ? "$" + job.hourly_rate + "/hr" : "$" + job.flat_rate + " flat"}
                              </span>
                            )}
                          </div>
                        </Link>
                        
                        {/* Match and Applicant counts */}
                        {job.status !== "withdrawn" && (
                          <div className="text-right space-y-1 shrink-0">
                            <div>
                              <span className="text-xl font-bold text-[#3498DB]">{job.match_count || 0}</span>
                              <span className="text-white/50 text-sm ml-1">matches</span>
                            </div>
                            <div>
                              <span className="text-xl font-bold text-coral">{job.applicant_count || 0}</span>
                              <span className="text-white/50 text-sm ml-1">applied</span>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Quick Actions */}
                      {actions.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/5">
                          {actions.map((act) => (
                            <Button 
                              key={act.action} 
                              size="sm" 
                              variant="outline" 
                              className={act.variant === "destructive" 
                                ? "border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs" 
                                : "border-white/20 text-white/60 hover:bg-white/10 text-xs"}
                              onClick={() => handleAction(job.job_id, act.action)}
                              data-testid={`job-action-${act.action}-${job.job_id}`}
                            >
                              <act.icon className="w-3.5 h-3.5 mr-1" />
                              {act.label}
                            </Button>
                          ))}
                          <Link to={`/jobs/${job.job_id}`} className="ml-auto">
                            <Button size="sm" variant="ghost" className="text-white/40 hover:text-white text-xs">
                              Details <ChevronRight className="w-3.5 h-3.5 ml-1" />
                            </Button>
                          </Link>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
}
